import React from 'react';
import styled from 'styled-components';

import BreadCrumbs from '@/components/atom/BreadCrumbs';
import Arrow from '@/components/atom/icon/Arrow/Arrow';
import CommunityCategoryBottomSheet from '@/components/common/CommunityCategoryBottomSheet';
import PostList from '@/components/common/PostList';
import WriteFixedBottomSheet from '@/components/common/WriteFixedBottomSheet';
import TemplateLayout from '@/components/layouts/TemplateLayout';
import ReportSelectModal from '@/components/molecules/Modal/SelectModal/ReportSelectModal';
import PullToRefreshContainer from '@/components/molecules/PullToRefresh';
import StickyAppHeader from '@/components/molecules/StickyAppHeader';
import useCommunityListViewModel from '@/hooks/community/useCommunityListViewModel';
import theme from '@/styles/theme';
import { ClientType, CustomPageProps } from '@/types/app';
import { DIRECTION, ROUTES } from '@/types/common';

const CommunityListTemplate = ({ client }: CustomPageProps) => {
  const viewModel = useCommunityListViewModel(client);

  return (
    <TemplateLayout.Wrapper>
      {client.isWebView && (
        <TemplateLayout.Header useSafeArea>
          <StickyAppHeader hasSearchButton={viewModel.onClickSearchIcon} title={'커뮤니티'} />
        </TemplateLayout.Header>
      )}
      <TemplateLayout.Content>
        {client.clientType === ClientType.WEB && (
          <BreadCrumbs.Wrapper>
            <BreadCrumbs
              visible
              path={`${ROUTES.COMMUNITY.LIST.path}`}
              type="community"
              name={viewModel.selectedCategory?.name ?? '전체'}
              urlParameter={`?categoryId=${viewModel.selectedCategory?.boardCategoryId}` ?? ''}
            />
          </BreadCrumbs.Wrapper>
        )}
        <Wrapper id={'postList'}>
          <CategoryWrapper>
            <Category>
              {viewModel.selectedCategory ? viewModel.selectedCategory.name : '전체'}
              <CategoryButton onClick={() => viewModel.onToggleBottomSheet()} type="button">
                카테고리
                <Arrow size={12} direction={DIRECTION.TOP} />
              </CategoryButton>
            </Category>
          </CategoryWrapper>

          <PullToRefreshContainer onRefresh={viewModel.onRefresh}>
            <PostList
              posts={{
                data: viewModel.newestPostsData?.pages,
                isLoading: viewModel.isNewestPostsLoading,
                hasNextPage: viewModel.hasNewestPostsNextPage,
                fetchNextPage: () => viewModel.fetchNewestPostsNextPage(),
              }}
              onClickReport={viewModel.onClickReport}
              onClickPost={viewModel.onClickPost}
            />
          </PullToRefreshContainer>
          <CommunityCategoryBottomSheet
            isOpened={viewModel.isVisibleBottomSheet}
            selectedCategoryId={viewModel.selectedCategory?.boardCategoryId}
            onClickCategory={viewModel.onClickCategory}
            onClose={() => viewModel.onToggleBottomSheet(false)}
            onClickAllCategory={viewModel.onClickAllCategory}
            isFilter
          />
          {viewModel.reportItems && (
            <ReportSelectModal
              isVisible={viewModel.showReportModal}
              onClose={viewModel.onCloseReportModal}
              selectList={viewModel.reportItems}
              title={viewModel.reportData?.isMine ? '게시글 관리' : '신고하기'}
            />
          )}

          <WriteFixedBottomSheet
            pageName={viewModel.pageName}
            selectedCategory={viewModel.selectedCategory}
            width={viewModel.fixedBottomSheetWidth}
          />
        </Wrapper>
      </TemplateLayout.Content>
    </TemplateLayout.Wrapper>
  );
};

export default CommunityListTemplate;

const Wrapper = styled.div`
  width: 100%;
  max-width: 768px;
`;

const CategoryWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Category = styled.div`
  display: flex;
  align-items: center;
  height: 60px;
  padding-left: 24px;
  ${({ theme }) => theme.typography.UI22SB};
`;

const CategoryButton = styled.button`
  display: flex;
  ${({ theme }) => theme.typography.UI14M};
  color: ${theme.color.gray600};
  align-content: center;
  justify-content: center;
  padding: 0 12px;
  height: 32px;
  background: ${theme.color.gray50};
  margin-left: 16px;
  border-radius: 8px;
  & > svg {
    margin-left: 8px;
  }
`;
